import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import UserInfoForm from './UserInfoForm';

import Chat from '@/components/chat/Chat';
import { formatVariantTitle } from '@/components/chat/utils/initial-messages';
import Loader from '@/components/ui/loader/Loader';
import { getThemeValue } from '@/components/utils/utils';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ChatBotSessionResponseDto } from '@/interfaces/api/chat/ChatBotSessionResponseDto';
import { ICreateChatPayload } from '@/interfaces/api/chat/ICreateChatPayload';
import { ICollectionHaggleEvent } from '@/interfaces/api/haggle-event/ICollectionHaggleEvent';
import { IProductVariant } from '@/interfaces/api/product/IProduct';
import { SortType } from '@/interfaces/haggle/SortType.enum';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

interface ProductOptions {
	name: string;
	position: number;
}

interface ChatPageProps {
	productId?: string;
	variantId?: string;
	localCheckout?: boolean;
	isCSVHaggle?: boolean;
	selectedVariant?: IProductVariant;
	vendorEmail?: string;
	productOptions?: ProductOptions[];
}

export default function ChatPage({
	productId: propProductId,
	variantId: propVariantId,
	localCheckout,
	isCSVHaggle,
	selectedVariant,
	vendorEmail,
	productOptions,
}: ChatPageProps) {
	const { productId: paramProductId, variantId: paramVariantId } = useParams();

	// Use props if available, otherwise fallback to useParams()
	const productId = propProductId ?? paramProductId;
	const variantId = propVariantId ?? paramVariantId;

	const [chat, setChat] = useState<ChatBotSessionResponseDto | undefined>(
		undefined,
	);
	const [title, setTitle] = useState(
		selectedVariant ? formatVariantTitle(selectedVariant, productOptions) : '',
	);
	const chatCreationAttempted = useRef(false);

	// User information form state
	const [showUserInfoForm, setShowUserInfoForm] = useState(true);
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [shareDetailsConsent, setShareDetailsConsent] = useState(true);

	const { data: haggleEvents } = useQuery<ICollectionHaggleEvent>({
		queryKey: [ApiQueryKeys.getAllHaggleEventByProductId, productId],
		queryFn: () =>
			haggleApiService.getAllHaggleEventByProductId({
				params: {
					'filter[productIds]': productId,
					'sort[endDate]': SortType.DESC,
				},
			}),
		enabled: productId !== undefined && !isNaN(Number(productId)),
	});

	const storeName: string | undefined = haggleEvents?.data[0]?.store?.name;
	const { data: store } = useQuery({
		queryKey: [ApiQueryKeys.getStoreThemeByName, storeName],
		queryFn: () => haggleApiService.getStoreThemeByName(storeName),
		enabled: !!storeName,
	});

	useEffect(() => {
		if (storeName && title === '') {
			const getTitle = async () => {
				const { title } = await haggleApiService.getVariantTitleByVariantId(
					variantId?.toString() ?? '',
					storeName,
				);
				setTitle(title);
			};

			getTitle();
		}
	}, [storeName, variantId]);

	const handleUserInfoFormSubmit = (
		name: string,
		email: string,
		shareDetailsConsent: boolean,
	) => {
		setShowUserInfoForm(false);
		setName(name);
		setEmail(email);
		setShareDetailsConsent(shareDetailsConsent);
	};

	const handleCreateSubmit = async (values: ICreateChatPayload) => {
		try {
			const newChat = isCSVHaggle
				? await haggleApiService.createLocalChat(values)
				: await haggleApiService.createChat(values);
			setChat(newChat);
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to create a chat: ${error}`,
				);
			}
		}
	};

	useEffect(() => {
		if (
			productId &&
			haggleEvents &&
			storeName &&
			variantId &&
			title !== '' &&
			!chatCreationAttempted.current &&
			!showUserInfoForm // Only create chat after user info form is submitted
		) {
			chatCreationAttempted.current = true; // prevent duplicate chat creations
			const createChat = async () => {
				if (!chat) {
					await handleCreateSubmit({
						productId: Number(productId),
						haggleEventId: haggleEvents?.data[0].id,
						storeName: storeName,
						variantId: variantId,
						variantTitle: title,
						consumerEmail:
							haggleEvents?.data[0].followUpOfferValidity !== null ||
							shareDetailsConsent
								? email
								: undefined,
						chatbotName: name,
						chatbotEmail: email,
					});
				}
			};

			createChat();
		}
	}, [productId, haggleEvents, storeName, variantId, title, showUserInfoForm]);

	return (
		<div data-test="chat-page" className="h-full">
			{showUserInfoForm && <UserInfoForm onSubmit={handleUserInfoFormSubmit} />}

			{!showUserInfoForm && !chat && (
				<Loader
					className="flex justify-center w-full h-full p-4"
					variant="scaleUp"
					size="large"
					showOnlyThreeDots={false}
					data-test="loader"
				/>
			)}

			{!showUserInfoForm && chat && storeName && (
				<Chat
					chatProp={chat}
					title={title}
					theme={store?.theme ? getThemeValue(store?.theme) : undefined}
					avatar={store?.avatarUrl}
					logo={store?.businessLogoUrl}
					redirectToLocalCheckout={localCheckout}
					productId={productId}
					storeName={storeName}
					isCSVHaggle={isCSVHaggle}
					vendorEmail={vendorEmail}
				/>
			)}
		</div>
	);
}
